import type { CSSProperties } from "react";
import { sidebarItems, sidebarFooterItems } from "./sidebar";

/**
 * Welche Farbe ein Bereich hat.
 *
 * Sie steht genau einmal da: neben dem Eintrag in `sidebar.tsx`. Vorher gab
 * es dieselbe Zuordnung ein zweites Mal in `Layout.tsx`, mit dem Hinweis,
 * beide von Hand synchron zu halten — drei Einträge waren es dann schon
 * nicht mehr, und die Ablage tönte sich sapphirblau, obwohl ihre Farbe
 * Petrol ist.
 *
 * Der Ton wird an zwei Stellen gebraucht: die Seite selbst färbt damit
 * Überschriften und Zeilen, und das Dashboard färbt damit die Kachel, die
 * dorthin führt. Beides ist dieselbe Auskunft — „hier geht es zu X" —, und
 * sie darf nicht zweimal verschieden beantwortet werden.
 */

/** Der Ton, wenn der Pfad zu keinem Bereich gehört. */
const VORGABE = "mauve";

/** Palettenname → das passende rgb-Tripel.
 *
 *  Nötig, weil ältere Regeln `rgba(var(--page-color-rgb), …)` schreiben.
 *  Neue Regeln brauchen das nicht mehr — `color-mix()` mischt direkt aus
 *  der Farbe. */
const RGB: Record<string, string> = {
  mauve: "--accent-rgb",
  blue: "--accent-2-rgb",
  lavender: "--accent-3-rgb",
  red: "--danger-rgb",
  maroon: "--danger-2-rgb",
  green: "--success-rgb",
  yellow: "--warning-rgb",
  peach: "--warning-2-rgb",
  sky: "--info-rgb",
  sapphire: "--info-2-rgb",
  teal: "--info-3-rgb",
  pink: "--soft-rgb",
  // Flamingo hat kein eigenes Tripel; Rosa ist der nächste Nachbar.
  flamingo: "--soft-rgb",
};

/** Alle Einträge, längster Pfad zuerst — damit „/git/foo" nicht bei „/"
 *  hängen bleibt und ein Unterpunkt vor seinem Elternteil gewinnt. */
const EINTRAEGE = [...sidebarItems, ...sidebarFooterItems]
  .flatMap((it) => [it, ...(it.children ?? [])])
  .filter((it) => it.path !== "/")
  .sort((a, b) => b.path.length - a.path.length);

/** Der Palettenname für einen Pfad. Eine Abfrage („/storage?m=1") oder ein
 *  Unterpfad („/git/home_server") zählt als derselbe Bereich. */
export function bereichsFarbe(pfad: string): string {
  const rein = pfad.split(/[?#]/)[0];
  for (const it of EINTRAEGE) {
    if (rein === it.path || rein.startsWith(it.path + "/")) {
      return it.color ?? VORGABE;
    }
  }
  return VORGABE;
}

/** Das rgb-Tripel zu einem Palettennamen. */
export function farbTripel(name: string): string {
  return RGB[name] ?? "--accent-rgb";
}

/** Beide Variablen auf einmal — zum Anhängen an ein Element. */
export function farbStil(pfadOderName: string, istName = false): CSSProperties {
  const name = istName ? pfadOderName : bereichsFarbe(pfadOderName);
  return {
    ["--page-color" as string]: `var(--${name})`,
    ["--page-color-rgb" as string]: `var(${farbTripel(name)})`,
  };
}
